import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import type { TraCertificationEdition } from "@/lib/api/traAdminClient"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { traNavTabs } from "@/components/tra/traNavTabs"
import { EyeIcon, PencilIcon } from "../../icons"

type Seedbed = { id: string; name: string }

function isOpen(edition: TraCertificationEdition): boolean {
  const now = Date.now()
  return now >= new Date(edition.registrationStartDate).getTime() && now <= new Date(edition.registrationDeadline).getTime()
}

export default function EditionsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [openFilter, setOpenFilter] = useState<"" | "open" | "closed">("")
  const [seedbeds, setSeedbeds] = useState<Seedbed[]>([])

  const {
    rows: editions,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<TraCertificationEdition>({
    endpoint: "/tra/certification-editions",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    // El estado viaja al servidor: el total y la paginación son reales con cualquier cantidad de ediciones.
    params: { search, open: openFilter === "open" ? "true" : openFilter === "closed" ? "false" : undefined },
    extractRows: (data) => data.items ?? [],
    extractTotal: (data) => data.total ?? 0,
  })

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin?limit=200&page=1")
      .then((res) => setSeedbeds(res.data?.data?.seedbeds ?? []))
      .catch(() => setSeedbeds([]))
  }, [])

  const seedbedName = (id: string | null) => (id ? seedbeds.find((s) => s.id === id)?.name ?? "—" : "—")
  const openCount = editions.filter(isOpen).length

  const columns: DataTableColumn<TraCertificationEdition>[] = [
    {
      key: "name",
      label: "Edición",
      sortable: true,
      render: (edition) => (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{edition.name}</p>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              isOpen(edition)
                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            }`}
          >
            {isOpen(edition) ? "Inscripción abierta" : "Cerrada"}
          </span>
        </>
      ),
    },
    {
      key: "certLevels",
      label: "Niveles",
      render: (edition) => <span className="text-gray-600 dark:text-gray-400">{edition.certLevels.join(", ")}</span>,
    },
    {
      key: "window",
      label: "Ventana de inscripción",
      render: (edition) => (
        <span className="text-xs text-gray-500">
          {new Date(edition.registrationStartDate).toLocaleDateString("es-CO")} →{" "}
          {new Date(edition.registrationDeadline).toLocaleDateString("es-CO")}
        </span>
      ),
    },
    {
      key: "seedbed",
      label: "Semillero",
      render: (edition) => <span className="text-gray-600 dark:text-gray-400">{seedbedName(edition.seedbedId)}</span>,
    },
    {
      key: "waitlist_count",
      label: "Candidatos",
      align: "center",
      render: (edition) => <span className="text-gray-600 dark:text-gray-400">{edition.waitlist_count ?? 0}</span>,
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (edition) => (
        <RowActionsMenu
          actions={[
            { label: "Ver candidatos", icon: <EyeIcon />, to: `/tra/editions/${edition.id}?tab=participantes` },
            { label: "Editar", icon: <PencilIcon />, to: `/tra/editions/${edition.id}`, requires: "tripoli:editions:edit" },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "TRA", "Ediciones"]}
      title="Ediciones de certificación"
      subtitle="Ciclos de certificación Tripoli Colombia — cada uno con su propia ventana de inscripción y semillero de aprendizaje"
      tabs={traNavTabs('editions')}
      metrics={[
        { label: "Total", value: String(total) },
        { label: "Abiertas (página)", value: String(openCount), tone: openCount > 0 ? "good" : "neutral" },
      ]}
      toolbar={{
        search: { value: search, onChange: setSearch, placeholder: "Buscar edición..." },
        filters: [
          {
            type: "select",
            key: "open",
            label: "Inscripción",
            value: openFilter,
            onChange: (v) => setOpenFilter(v as "" | "open" | "closed"),
            options: [
              { label: "Todas", value: "" },
              { label: "Abierta", value: "open" },
              { label: "Cerrada", value: "closed" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,
        onCreate: () => navigate("/tra/editions/new"),
        createLabel: "+ Nueva edición",
        requires: "tripoli:editions:edit",
      }}
    >
      <DataTable
        columns={columns}
        rows={editions}
        rowKey={(edition) => edition.id}
        loading={loading}
        emptyMessage="No se han creado ediciones todavía."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
